import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from '../../services/api/client'
import type {
  InteractionActionRequest,
  InteractionOperationalState,
} from './interaction-operational-contracts'
import type { InteractionOperationalRepository } from './interaction-operational-repository'

// Staff own every transition: completing resolves a request, reopening
// corrects a completion. Each command is compare-and-set on the revision the
// staff member saw, so a click never overwrites a newer decision; a conflict
// reloads and shows what is true now. All state is per conversation, so a
// command still running for one never disturbs the next one selected.

export type ActionRequestsLoad = {
  status: 'idle' | 'loading' | 'ready' | 'error'
  items: InteractionActionRequest[]
  // The server's word on whether staff may complete, undo and reopen here.
  lifecycleEnabled: boolean
}

export type ActionRequestOutcome = {
  kind: 'COMPLETED' | 'REOPENED'
  // The records exactly as the commands left them.
  items: InteractionActionRequest[]
}

export type ActionRequestNotice = {
  message: string
  tone: 'conflict' | 'failure'
}

type Target = { owner: string; tenantId: string; aggregateId: string }

const IDLE: ActionRequestsLoad = {
  status: 'idle',
  items: [],
  lifecycleEnabled: false,
}

function conflictMessage(count: number) {
  return count === 1
    ? 'This request changed since you opened it. Showing the latest.'
    : 'Some of these requests changed since you opened them. Showing the latest.'
}

function without<T>(values: Record<string, T>, key: string) {
  const next = { ...values }
  delete next[key]
  return next
}

export function useActionRequests({
  repository,
  tenantId,
  aggregateId,
  operationalState,
  statusRefreshes = 0,
  activityAtMs,
  onReviewed,
  onChanged,
}: {
  repository: InteractionOperationalRepository | undefined
  tenantId: string
  aggregateId: string | null
  operationalState: InteractionOperationalState | null
  // Completed refreshes of the conversations' status.
  statusRefreshes?: number
  activityAtMs: number | null
  onReviewed: (state: InteractionOperationalState) => void
  onChanged: (aggregateId: string) => Promise<void>
}) {
  const owner = aggregateId ? `${tenantId}:${aggregateId}` : null
  const readable = Boolean(repository?.listActionRequests)
  const commandable = Boolean(
    repository?.resolveActionRequest && repository.reopenActionRequest
  )
  const [loads, setLoads] = useState<Record<string, ActionRequestsLoad>>({})
  const [busy, setBusy] = useState<Record<string, 'complete' | 'reopen'>>({})
  const [outcomes, setOutcomes] = useState<
    Record<string, ActionRequestOutcome>
  >({})
  // A conflict is news (someone else acted first), not a failure.
  const [notices, setNotices] = useState<Record<string, ActionRequestNotice>>(
    {}
  )
  const generations = useRef<Record<string, number>>({})
  const running = useRef<Set<string>>(new Set())
  const callbacks = useRef({ onReviewed, onChanged })
  callbacks.current = { onReviewed, onChanged }

  const reloadTarget = useCallback(
    async (target: Target) => {
      if (!repository?.listActionRequests) return
      const attempt = (generations.current[target.owner] ?? 0) + 1
      generations.current[target.owner] = attempt
      // Keep showing this conversation's last good list while it refreshes.
      setLoads((current) => ({
        ...current,
        [target.owner]:
          current[target.owner]?.status === 'ready'
            ? current[target.owner]!
            : { ...(current[target.owner] ?? IDLE), status: 'loading' },
      }))
      try {
        const result = await repository.listActionRequests(
          target.tenantId,
          target.aggregateId
        )
        if (generations.current[target.owner] !== attempt) return
        setLoads((current) => ({
          ...current,
          [target.owner]: {
            status: 'ready',
            items: result.items,
            lifecycleEnabled: result.staffLifecycleEnabled,
          },
        }))
      } catch {
        if (generations.current[target.owner] !== attempt) return
        setLoads((current) => ({
          ...current,
          [target.owner]: {
            ...(current[target.owner] ?? IDLE),
            status: 'error',
          },
        }))
      }
    },
    [repository]
  )

  const target = owner && aggregateId ? { owner, tenantId, aggregateId } : null
  const reload = useCallback(
    () => (target ? reloadTarget(target) : Promise.resolve()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owner, reloadTarget]
  )

  // Re-read whenever the open set changes, whoever changed it.
  const openKey = operationalState
    ? `${operationalState.unresolvedActionRequestCount}:${operationalState.unresolvedActionRequestTypes.join(',')}`
    : null
  useEffect(() => {
    if (!readable || !owner) return
    void reload()
  }, [owner, openKey, readable, reload])

  // And with every status refresh, whatever the list held: a change made
  // elsewhere can leave the open count and types as they were (one completed
  // while another was raised, an Undo, or a request raised and completed
  // between two refreshes of an empty list). Status refreshes follow change
  // hints, so a read still in flight may predate the change: a new read
  // supersedes it rather than being skipped.
  useEffect(() => {
    if (!readable || !owner || statusRefreshes === 0) return
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusRefreshes])

  const finish = useCallback(
    async (
      settled: Target,
      notice: ActionRequestNotice | null,
      outcome: ActionRequestOutcome | null
    ) => {
      setOutcomes((current) =>
        outcome
          ? { ...current, [settled.owner]: outcome }
          : without(current, settled.owner)
      )
      setNotices((current) =>
        notice
          ? { ...current, [settled.owner]: notice }
          : without(current, settled.owner)
      )
      try {
        await reloadTarget(settled)
        await callbacks.current.onChanged(settled.aggregateId)
      } finally {
        running.current.delete(settled.owner)
        setBusy((current) => without(current, settled.owner))
      }
    },
    [reloadTarget]
  )

  const begin = (kind: 'complete' | 'reopen') => {
    if (!target || running.current.has(target.owner)) return null
    running.current.add(target.owner)
    setBusy((current) => ({ ...current, [target.owner]: kind }))
    setNotices((current) => without(current, target.owner))
    return target
  }

  const complete = async (requests: InteractionActionRequest[]) => {
    const resolve = repository?.resolveActionRequest
    const open = requests.filter((item) => item.status === 'OPEN')
    if (!repository || !resolve || open.length === 0) return
    const current = owner ? (loads[owner]?.items ?? []) : []
    const stillOpen = current.filter(
      (item) =>
        item.status === 'OPEN' &&
        !open.some((chosen) => chosen.actionRequestId === item.actionRequestId)
    ).length
    // Captured at the decision: the review covers what staff were looking at.
    const review =
      operationalState &&
      activityAtMs &&
      operationalState.reviewStatus !== 'REVIEWED'
        ? { revision: operationalState.reviewRevision, activityAtMs }
        : null
    const settled = begin('complete')
    if (!settled) return
    const done: InteractionActionRequest[] = []
    let conflicts = 0
    let failures = 0
    for (const item of open) {
      try {
        done.push(
          await resolve(settled.tenantId, item.actionRequestId, item.revision)
        )
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 409) conflicts += 1
        else failures += 1
      }
    }
    let notice: ActionRequestNotice | null = failures
      ? {
          message: done.length
            ? 'Some requests couldn’t be marked completed. Try again.'
            : 'The action couldn’t be marked completed. Try again.',
          tone: 'failure',
        }
      : conflicts
        ? { message: conflictMessage(conflicts), tone: 'conflict' }
        : null
    // Completing the last open request also settles review, so the
    // conversation never reappears as Needs review for the same activity.
    if (done.length === open.length && stillOpen === 0 && review) {
      try {
        callbacks.current.onReviewed(
          await repository.markReviewed(
            settled.tenantId,
            settled.aggregateId,
            review.revision,
            review.activityAtMs,
            true
          )
        )
      } catch (caught) {
        if (!(caught instanceof ApiError && caught.status === 409)) {
          notice = {
            message:
              'The action is completed, but the review couldn’t be recorded.',
            tone: 'failure',
          }
        }
      }
    }
    await finish(
      settled,
      notice,
      done.length ? { kind: 'COMPLETED', items: done } : null
    )
  }

  const reopen = async (requests: InteractionActionRequest[]) => {
    const reopenRequest = repository?.reopenActionRequest
    const resolved = requests.filter(
      (item) => item.status === 'RESOLVED' && item.reopenable
    )
    if (!reopenRequest || resolved.length === 0) return
    const settled = begin('reopen')
    if (!settled) return
    const done: InteractionActionRequest[] = []
    let conflicts = 0
    let refused = 0
    let failures = 0
    for (const item of resolved) {
      try {
        done.push(
          await reopenRequest(
            settled.tenantId,
            item.actionRequestId,
            item.revision
          )
        )
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 409) conflicts += 1
        // The clinic's lifecycle was turned off since this list was read;
        // the reload that follows hides the controls.
        else if (caught instanceof ApiError && caught.status === 403)
          refused += 1
        else failures += 1
      }
    }
    await finish(
      settled,
      refused
        ? {
            message: 'Reopening requests isn’t enabled for this clinic.',
            tone: 'failure',
          }
        : failures
          ? {
              message: done.length
                ? 'Some requests couldn’t be reopened. Try again.'
                : 'The request couldn’t be reopened. Try again.',
              tone: 'failure',
            }
          : conflicts
            ? { message: conflictMessage(conflicts), tone: 'conflict' }
            : null,
      done.length ? { kind: 'REOPENED', items: done } : null
    )
  }

  const requests = (owner && loads[owner]) || IDLE
  const outcome = owner ? (outcomes[owner] ?? null) : null
  // Undo is offered only while the session's own completion still stands.
  const undoable =
    outcome?.kind === 'COMPLETED' &&
    requests.status === 'ready' &&
    outcome.items.every((done) =>
      requests.items.some(
        (item) =>
          item.actionRequestId === done.actionRequestId &&
          item.status === 'RESOLVED' &&
          item.reopenable &&
          item.revision === done.revision
      )
    )
      ? outcome.items
      : null

  return {
    // The conversation's requests can be read here.
    readable,
    // Staff may complete, undo and reopen here: the client can, the server
    // enabled the lifecycle for this clinic, and the conversation is a
    // canonical row. Completion is offered only with its operational state,
    // so Reopen is never offered without it (a legacy row has none).
    supported:
      readable &&
      commandable &&
      requests.lifecycleEnabled &&
      operationalState !== null,
    requests,
    reload,
    complete,
    reopen,
    undo: () => (undoable ? reopen(undoable) : Promise.resolve()),
    undoable: undoable !== null,
    busy: owner ? (busy[owner] ?? null) : null,
    notice: owner ? (notices[owner] ?? null) : null,
  }
}

export type ActionRequestsView = ReturnType<typeof useActionRequests>
